import type { CheckResult } from './types';

/**
 * Which of the loop's two result lists is the one on screen.
 *
 * `useCheckLoop` keeps them apart on purpose. `live` is the current cycle's
 * latest verdict per link, cleared when a cycle starts; `results` is the
 * aggregated batch that was actually submitted. While a run is in flight only
 * `live` is moving. Once it finishes, `results` is the honest answer because it
 * is the one the backend was told — it has been through `aggregate`, so a link
 * that failed fewer rounds than configured has been dropped rather than
 * condemned. Falling back to `live` covers a cycle that failed before it could
 * submit: showing what that run did manage to see beats showing the screen it
 * had before it started.
 *
 * **One rule, one copy.** This was written out twice — once in the shell that
 * feeds the Results tab and once in the dashboard — with a comment in one of
 * them stating that the two *must* agree and then declining to enforce it.
 * They must: "See all" is a promise that the Results tab is the long version of
 * the four rows on Home, and a Results tab showing the previous batch while Home
 * counts the running one is a discrepancy an operator reads as data loss.
 *
 * Returns the caller's own `results` array rather than a copy when it wins, so
 * the identity a `useMemo` downstream depends on is preserved and a
 * several-hundred-row list is not re-allocated for nothing.
 */
export function selectShownResults(
  running: boolean,
  results: CheckResult[],
  live: Map<string, CheckResult>,
): CheckResult[] {
  if (!running && results.length > 0) return results;
  return Array.from(live.values());
}
